import React from 'react';
import Image from 'next/image';

const professions = [
  {
    image: '/images/privacy/professions/attorneys.jpg',
    imageAlt: 'Leather-bound law volumes and a brass scale of justice under a desk lamp',
    imagePosition: 'center 60%',
    title: 'Attorneys',
    duty: 'ABA Rule 1.6 · Formal Op. 512',
    body: 'You hold privilege for a living — and courts have ruled that work done in consumer AI tools may carry none. Draft, research, and pressure-test arguments through a protected Bedrock path with zero-retention inference and no model-provider training on inputs or outputs.',
  },
  {
    image: '/images/privacy/professions/cpas.jpg',
    imageAlt: 'A fountain pen resting on open ledger pages beside a vintage brass calculator',
    imagePosition: 'center 55%',
    title: 'CPAs & tax professionals',
    duty: 'IRC §7216 · AICPA confidentiality',
    body: 'Disclosing tax return information without consent is not just an ethics issue — it can be a federal offense. Analyze client financials and draft memos without handing the numbers to a chatbot that stores them on someone else’s terms.',
  },
  {
    image: '/images/privacy/professions/healthcare.jpg',
    imageAlt: 'A stethoscope coiled on a clinic desk beside a clipboard chart',
    imagePosition: 'center 75%',
    title: 'Physicians & healthcare teams',
    duty: 'HIPAA',
    body: 'Consumer chatbots are not built for protected health information. Chat.co adds authenticated access, role-based controls, encryption, and a protected Bedrock inference path for sensitive clinical knowledge.',
  },
  {
    image: '/images/privacy/professions/financial-advisors.jpg',
    imageAlt: 'Reading glasses on a paper chart before a dusk city skyline',
    imagePosition: 'center 45%',
    title: 'Financial advisors',
    duty: 'SEC Reg S-P · FINRA',
    body: 'Client holdings, plans, and personal data are regulated the moment they leave your hands. Get AI help with analysis and client communication while keeping knowledge governed, exportable, and deletable.',
  },
  {
    image: '/images/privacy/professions/hr-teams.jpg',
    imageAlt: 'A meeting room table with closed notebooks and water glasses in morning light',
    imagePosition: 'center 60%',
    title: 'HR & people teams',
    duty: 'GDPR · state privacy law',
    body: 'Compensation data, investigations, and health accommodations are exactly the conversations that should never surface in someone else’s training set. Role-based access and domain restriction keep sensitive people-work contained.',
  },
  {
    image: '/images/privacy/professions/executives.jpg',
    imageAlt: 'A dark boardroom table under warm pendant lights with a city at dusk beyond',
    imagePosition: 'center 30%',
    title: 'Executives & boards',
    duty: 'Fiduciary duty · trade secrets',
    body: 'Strategy documents, M&A thinking, and unannounced numbers are one careless paste away from living on a consumer server — discoverable, and outside your control. Work through hard decisions with AI that keeps them confidential.',
  },
];

const WhoItsFor: React.FC = () => (
  <section aria-label="Who Chat.co privacy is for" className="bg-white pb-20 sm:pb-24">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl">
        <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#b8862e]">
          Who it&rsquo;s for
        </span>
        <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
          Built for people who are{' '}
          <span className="font-display font-medium italic text-[#004790]">bound</span> to
          confidentiality.
        </h2>
        <p className="mt-4 text-lg leading-relaxed text-gray-500">
          If a rule, a license, or a duty stands behind your work, &ldquo;the chatbot kept a
          copy&rdquo; is not an acceptable answer. Chat.co is built for professions where privacy is
          not a preference &mdash; it&rsquo;s an obligation.
        </p>
      </div>

      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {professions.map((profession) => (
          <div
            key={profession.title}
            className="group flex flex-col overflow-hidden rounded-2xl border border-gray-100 bg-gray-50/50 transition-all duration-300 hover:-translate-y-1 hover:border-blue-100 hover:bg-white hover:shadow-lg hover:shadow-blue-500/5">
            <div className="relative h-28 overflow-hidden">
              <Image
                src={profession.image}
                alt={profession.imageAlt}
                width={960}
                height={640}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                style={{ objectPosition: profession.imagePosition }}
              />
              <span className="absolute bottom-3 right-3 rounded-full border border-white/15 bg-[#04182e]/75 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
                {profession.duty}
              </span>
            </div>
            <div className="flex flex-col p-7 pt-5">
              <h3 className="text-lg font-bold text-gray-900">{profession.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-500">{profession.body}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default WhoItsFor;
